import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom';
import { translate } from '../utils/translator';
import CartViewSummary from './CartViewSummary';
import { useCartContext } from '../customHooks/useCart';
import RiskGroupedProductList from './RiskGroupedProductList';
import { groupProductsByRiskGroup } from '../utils/groupProductsByRiskGroup';
import { EXTERNAL_PHARMACY_INTEGRATIONS_FLOWS } from '../utils/consts';
import CartViewSubscriptionPlans from './CartViewSubscriptionPlans';
import VideoConsultationRow from './VideoConsultationRow';
import UpsellUpgradeBanner from './UpsellUpgradeBanner';
import { useShippingPageContext } from '../pages/v2/ShippingV2/providers/ShippingPageProvider';
import RxUpsellRegretBanner from './RxUpsellRegretBanner';

type Props = {
  className?: string
  showProductsQuantityControl?: boolean
  hideSubscriptionPlans?: boolean
  trackFinalCartPrice?: boolean
  shippingFee?: number
  setRxProductFinalPrice?: (price: number) => void //TODO: Delete it when old shipping is dead
  isExpressShipping?: boolean
  OTCAddonsTrigger: React.ReactNode
  showDelayedCaptureDisclaimer?: boolean
}

const CartView: React.FC<Props> = ({ className, hideSubscriptionPlans = false, showProductsQuantityControl=true , setRxProductFinalPrice, shippingFee, trackFinalCartPrice=false, isExpressShipping=false, OTCAddonsTrigger, showDelayedCaptureDisclaimer=false }) => {

  const { language } = useParams()
  const { getCart } = useCartContext();
  const cart = getCart();
  const {
  setShowUpsellPopUp,
  eligibleForUpsell,
  wasUpgradedToPremium,
  showRxRegretBanner,
  setShowRxPremiumUpsellPopup,
  handleRxRegretDismiss,
  country,
} = useShippingPageContext();

  const rxProducts = useMemo(() => {
    return groupProductsByRiskGroup(cart.products.filter((product) => product.is_prescription_drug))
  }, [cart.products])
  const addonProducts = useMemo(() => {
    return groupProductsByRiskGroup(cart.products.filter((product) => !product.is_prescription_drug))
  }, [cart.products])
  
  return (
    <>
    <div id='cart-view-container' className={`flex flex-col bg-white px-5 rounded-[16px] h-fit gap-6 ${className} `}>
      {/* Desktop version - hidden on mobile */}
      <div className="hidden lg:block">
        {eligibleForUpsell && !wasUpgradedToPremium && <UpsellUpgradeBanner onUpgradeClick={() => setShowUpsellPopUp(true)} />}
      </div>

      {showRxRegretBanner && (
        <RxUpsellRegretBanner
          country={country}
          onUpgradeClick={() => setShowRxPremiumUpsellPopup(true)}
          onDismiss={handleRxRegretDismiss}
        />
      )}

      <div id='cart-view-products-container' className='flex flex-col gap-2'>
        <h2 className='text-base font-roboto font-semibold text-[#0D2C54]'>{translate(language, "default", "OrderWindow/prescription_products")}</h2>
         {/* Rx Product rows  */}
         <div style={{ borderBottom: "1px dashed #9D9D9D" }}>
          <RiskGroupedProductList products={rxProducts} wasUpgradedToPremium={wasUpgradedToPremium} />
        </div>
        {/* Subscription plans */}
        {hideSubscriptionPlans ? null : <CartViewSubscriptionPlans />}

        {addonProducts.length > 0 && (
        <>
          <h2 className='text-base font-roboto font-semibold text-[#0D2C54] mt-2'>{translate(language, "default", "OrderWindow/non_prescription_products")}</h2>
          {/* Addons products of the order (OTC, Cannabis card...)   */}
          <RiskGroupedProductList products={addonProducts} showQuantityControl={(product) => showProductsQuantityControl && !["cannabis_id", "schengen_id", "video_consultation", "test_kit"].includes(product.type!)} />
        </>
        )}
        {OTCAddonsTrigger && OTCAddonsTrigger}
      </div>

      {/* Video consultation */}
      {cart.consultation_type === "video" && (
        <VideoConsultationRow />
      )}
    </div>

    {/* Calculations Summary */}
    <div className={`flex flex-col bg-white px-5 pb-2 rounded-[16px] h-fit gap-6 ${className} `}
    >
      <div style={{ borderTop: "1px dashed #0A9281" }} className='pt-4'></div>
      <CartViewSummary setRxProductFinalPrice={setRxProductFinalPrice} trackFinalCartPrice={trackFinalCartPrice} shippingFee={shippingFee} isExpressShipping={isExpressShipping} showDelayedCaptureDisclaimer={showDelayedCaptureDisclaimer} />
    </div>

    {/* Images disclaimer */}
    {(!EXTERNAL_PHARMACY_INTEGRATIONS_FLOWS.includes(cart.flow || "")) ?
      <p
        id='cart-view-images-disclaimer'
        className={`text-xs text font-roboto text-center text-[#98a0af] mt-4 ${className} `}>*{translate(language, "default", "OrderWindow/image_disclaimer")}</p>
      :
      null
    }

    </>
  )
}

export default CartView